import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFileUpload } from '../../contexts/FileUploadContext';
import FileUtil from '../../utils/FileUtil';
import trackingUtil from '../../utils/TrackingUtil';
import { v1 as uuidv1 } from 'uuid';
import { projectKeys } from '../projects/queries';
import { normalizeId } from '../utils/idNormalization';
import { useUploadSingleFile } from './useUploadSingleFile';

export const useUploadFiles = () => {
  const { addFiles } = useFileUpload();
  const queryClient = useQueryClient();
  const uploadSingleFileMutation = useUploadSingleFile();

  return useMutation({
    mutationFn: async ({ projectId, files }) => {
      // Validate files - this will throw if invalid
      FileUtil.checkFiles(files);
      
      trackingUtil.logEvent('drag-drop-transcripts', {
        file_count: files.length
      });

      // Create file entries with unique IDs
      const filesWithIds = files.map(file => ({
        id: uuidv1(),
        file: file
      }));

      // Add all files to context with initial state
      const initialFiles = {};
      filesWithIds.forEach(({ id, file }) => {
        initialFiles[id] = {
          id,
          fileName: file.name,
          loaded: false,
          error: null
        };
      });
      addFiles(initialFiles);

      // Upload files sequentially
      const results = [];
      for (const { id, file } of filesWithIds) {
        try {
          const result = await uploadSingleFileMutation.mutateAsync({
            projectId,
            file,
            fileId: id
          });
          results.push(result);
        } catch (error) {
          // Error is already handled by the single file mutation
          console.error(`Failed to upload ${file.name}:`, error);
        }
      }
      
      return { success: true, projectId, results };
    },
    
    onSuccess: (data) => {
      // Invalidate the project query to ensure everything is in sync
      // The individual uploads already updated the cache, but this ensures consistency
      if (data.projectId) {
        queryClient.invalidateQueries({ 
          queryKey: projectKeys.detail(normalizeId(data.projectId))
        });
      }
    },
    
    onError: (error) => {
      console.error('File upload batch failed:', error);
    }
  });
};